import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Sparkles, MapPin, FileText } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { CLASSINA_PRODUCTS } from "@/data/products";

const PAPER_BG =
  "https://static.prod-images.emergentagent.com/jobs/73beafd6-f905-4c03-a95d-f6ba4441dadf/images/0c356d107f435b33125212a138d30a2d80846280593738d34e4b77d59b404e59.png";

const ClassinaPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Classina® · Our House Brand · SBK Manufacturing Co.";
  }, []);

  const classinaProducts = CLASSINA_PRODUCTS;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar transparentOnTop={false} />

      {/* Brand Hero */}
      <section
        data-testid="classina-hero"
        className="pt-32 md:pt-40 pb-20 md:pb-28 paper-grain border-b border-stone-200"
        style={{
          backgroundImage: `url(${PAPER_BG})`,
          backgroundSize: "cover",
          backgroundBlendMode: "multiply",
          backgroundColor: "#F6F4EF",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-px bg-ink-900/40" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
              Our House Brand
            </span>
          </div>
          <img
            src="/classina-logo.png"
            alt="Classina Products"
            className="h-16 md:h-24 w-auto mb-10 select-none"
            draggable="false"
          />
          <h1 className="font-serif font-light text-ink-900 text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight max-w-5xl">
            Designed in Pune. Made for{" "}
            <em className="italic text-terracotta-600">every desk</em>, every
            classroom, every diary.
          </h1>
          <p className="mt-8 max-w-3xl text-lg md:text-xl text-stone-800 leading-relaxed font-light">
            Classina® is the consumer face of SBK Manufacturing Company — a
            registered Indian stationery brand carrying our craftsmanship
            straight to the desk. From vibrant softbound notebooks for every
            classroom to our newest sustainable Eco Series printed on
            recyclable kraft, Classina is how we put paper into people's hands.
          </p>

          {/* Brand pillars */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
            {[
              {
                icon: MapPin,
                k: "Made in India",
                v: "Manufactured end-to-end in our Pune facility — paper to packaging.",
              },
              {
                icon: Leaf,
                k: "Eco-conscious",
                v: "Recyclable kraft, FSC paper and low-impact processes — Write Green, Live Clean.",
              },
              {
                icon: Sparkles,
                k: "Built to last",
                v: "Smooth-writing paper, sturdy bindings and covers that survive the school year.",
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.k}
                  className="bg-white border border-stone-200 rounded-sm p-7"
                >
                  <div className="w-10 h-10 rounded-sm bg-ink-900 text-stone-50 grid place-items-center mb-5">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="font-serif text-2xl text-ink-900 leading-tight">
                    {p.k}
                  </div>
                  <p className="mt-3 text-stone-700 text-sm leading-relaxed">
                    {p.v}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-px bg-ink-900/40" />
                <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
                  The Classina Range
                </span>
              </div>
              <h2 className="font-serif font-light text-ink-900 text-4xl md:text-5xl leading-tight tracking-tight">
                Products —{" "}
                <em className="italic">growing every season.</em>
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="classina-view-catalogue"
              className="inline-flex items-center gap-2 text-ink-900 font-medium border-b border-ink-900 pb-1 hover:gap-4 transition-all w-max"
            >
              See SBK's full catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {classinaProducts.length === 0 ? (
            <div className="text-center py-20 text-stone-700">
              No products tagged "Classina" yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {classinaProducts.map((p, i) => (
                <ProductCard
                  key={p.slug}
                  product={p}
                  eager={i < 4}
                  slugPrefix="classina"
                  compact
                />
              ))}

              {/* Coming soon placeholder */}
              <article
                data-testid="classina-coming-soon"
                className="border border-dashed border-stone-300 rounded-sm flex flex-col items-center justify-center text-center p-6 min-h-[280px] bg-stone-50/60"
              >
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-700 mb-2">
                  Coming Soon
                </span>
                <h3 className="font-serif text-lg text-ink-900 leading-tight">
                  More from <em className="italic">Classina</em>
                </h3>
                <p className="mt-2 text-stone-700 text-xs leading-relaxed max-w-xs">
                  Hardbound editions, premium diaries and creative-grade sketch
                  pads — launching across 2026.
                </p>
              </article>
            </div>
          )}
        </div>
      </section>

      {/* Lever Arch Box Files — dedicated collection section */}
      <LeverArchSection />

      {/* CTA */}
      <section className="bg-ink-900 text-stone-50 py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif font-light text-stone-50 text-4xl md:text-5xl leading-tight tracking-tight">
            Stock <em className="italic text-terracotta-500">Classina</em> at
            your store.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-stone-50/80 leading-relaxed">
            Distributors, retailers and corporate gifting partners — drop us a
            line for trade enquiries, sample kits and pricing.
          </p>
          <Link
            to="/?inquiry=Classina%20Trade%20Enquiry#contact"
            data-testid="classina-cta-contact"
            className="mt-10 inline-flex items-center gap-2 bg-stone-50 text-ink-900 px-8 py-4 rounded-sm font-medium hover:bg-stone-100 transition-colors"
          >
            Become a Classina partner <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClassinaPage;

// ============================================================
// Lever Arch Box Files — color-swatch hero section
// ============================================================
const LEVER_ARCH_VARIANTS = [
  {
    id: "black",
    label: "Black",
    swatch: "#1a1a1a",
    img: "/uploads/classina-lever-arch-black.jpg",
  },
  {
    id: "green",
    label: "Green",
    swatch: "#2f7a4a",
    img: "/uploads/classina-lever-arch-green.jpg",
  },
  {
    id: "royal-blue",
    label: "Royal Blue",
    swatch: "#1d3aa0",
    img: "/uploads/classina-lever-arch-royal-blue.jpg",
  },
  {
    id: "red",
    label: "Red",
    swatch: "#c43631",
    img: "/uploads/classina-lever-arch-red.jpg",
  },
  {
    id: "detail",
    label: "Inner Pattern",
    swatch: "#e9e9ea",
    swatchBorder: "#1a2332",
    img: "/uploads/classina-lever-arch-detail.jpg",
  },
];

const LEVER_ARCH_FEATURES = [
  {
    k: "Foolscap size",
    v: "Holds A4 & legal documents with margin for tabs.",
  },
  {
    k: "2-Ring lever mechanism",
    v: "Smooth open-and-close action; nickel-finish hardware.",
  },
  {
    k: "Heavy board cover",
    v: "Marble laminate finish in 4 colours — wipes clean, resists scuffs.",
  },
  {
    k: "Dual-label spine",
    v: "Pre-printed “File No.” block + blank rows for custom labels.",
  },
];

const LeverArchSection = () => {
  const [active, setActive] = useState("black");
  const current =
    LEVER_ARCH_VARIANTS.find((v) => v.id === active) || LEVER_ARCH_VARIANTS[0];

  return (
    <section
      id="lever-arch-files"
      data-testid="classina-lever-arch-section"
      className="bg-stone-100 border-y border-stone-200 py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Hero product image + swatches */}
          <div className="lg:col-span-5">
            <div className="relative bg-white border border-stone-200 rounded-sm p-6 md:p-10 shadow-sm">
              <img
                key={current.id}
                src={current.img}
                alt={`Classina Signature Lever Arch Box File — ${current.label} finish`}
                className="w-full h-auto object-contain max-h-[520px] mx-auto animate-fade-in"
                data-testid="lever-arch-hero-image"
              />
              <span className="absolute top-4 left-4 font-mono text-[9px] tracking-[0.2em] uppercase text-stone-600 bg-stone-50 border border-stone-200 px-2 py-1 rounded-sm">
                Foolscap · 2-Ring
              </span>
              <span
                className="absolute bottom-4 right-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-ink-900 text-stone-50 px-2.5 py-1 rounded-sm"
                data-testid="lever-arch-current-label"
              >
                {current.label}
              </span>
            </div>

            {/* Swatches */}
            <div
              className="mt-6 flex items-center justify-center lg:justify-start gap-3 flex-wrap"
              data-testid="lever-arch-swatches"
              role="radiogroup"
              aria-label="Choose Lever Arch File colour"
            >
              {LEVER_ARCH_VARIANTS.map((v) => {
                const isActive = v.id === active;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setActive(v.id)}
                    role="radio"
                    aria-checked={isActive}
                    aria-label={`Show ${v.label} variant`}
                    data-testid={`lever-arch-swatch-${v.id}`}
                    className={`group flex flex-col items-center gap-2 transition-transform ${
                      isActive ? "scale-105" : "hover:scale-105"
                    }`}
                  >
                    <span
                      className={`block w-10 h-10 rounded-full transition-all ${
                        isActive
                          ? "ring-2 ring-offset-2 ring-ink-900 ring-offset-stone-100"
                          : "ring-1 ring-stone-300"
                      }`}
                      style={{
                        background: v.swatch,
                        border: v.swatchBorder
                          ? `1px solid ${v.swatchBorder}`
                          : "none",
                      }}
                    />
                    <span
                      className={`font-mono text-[9px] tracking-[0.2em] uppercase ${
                        isActive ? "text-ink-900" : "text-stone-600"
                      }`}
                    >
                      {v.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Copy + features */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-ink-900/40" />
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-stone-700">
                New · Office &amp; Filing · 4 Colours
              </span>
            </div>
            <h2 className="font-serif font-light text-ink-900 text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Lever Arch{" "}
              <em className="italic text-terracotta-600">Box Files</em>
            </h2>
            <p className="mt-6 text-stone-800 text-base md:text-lg leading-relaxed max-w-2xl">
              Built for the long haul — Classina lever arch files keep
              contracts, ledgers and invoices archived for years. Heavy-duty
              board covers, a reinforced two-ring mechanism and a finger-pull
              eyelet on every spine. Choose from four marble-finish colours.
            </p>

            {/* Feature grid */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {LEVER_ARCH_FEATURES.map((f) => (
                <div
                  key={f.k}
                  className="bg-white border border-stone-200 rounded-sm p-4"
                  data-testid={`lever-arch-feature-${f.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-terracotta-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-serif text-ink-900 text-lg leading-tight">
                        {f.k}
                      </div>
                      <p className="mt-1 text-stone-700 text-sm leading-relaxed">
                        {f.v}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/?inquiry=Classina%20Lever%20Arch%20Box%20Files#contact"
                data-testid="lever-arch-enquire"
                className="inline-flex items-center gap-2 bg-ink-900 text-stone-50 px-7 py-3.5 rounded-sm font-medium text-sm hover:bg-ink-800 hover:gap-3 transition-all"
              >
                Enquire for Bulk &amp; OEM <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-stone-600">
                Custom branding · Private label available
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
